import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback
} from 'react';
import * as styles from './Form.module.scss';
import useConfig from '@hooks/useConfig';
import { upperfirst } from 'lodash';

export default React.memo(function FormWrapper({
  product,
  loanDetails,
  className,
  selector,
  header,
  hasPrefillFields = true
}) {
  const config = useConfig();
  const [hasAddedForm, setHasAddedForm] = useState(false);
  const wrapper = useRef();
  const form = useMemo(() => {
    return <Form
      setHasAddedForm={setHasAddedForm}
      selector={selector}
    />;
  }, [selector]);

  const isMutatingDOM = useRef(false);
  const mutateDOM = useCallback(callback => {
    isMutatingDOM.current = true;
    callback();
    isMutatingDOM.current = false;
  }, []);

  const toggleSelectPlaceholder = useCallback(select => {
    select?.classList.toggle(styles.placeholder, !select.value);
  }, []);

  const hiddenFields = useRef([]);

  const initElements = useCallback(() => {
    mutateDOM(() => {
      isMutatingDOM.current = true;

      wrapper.current
        .querySelectorAll('input[id*=PhoneNumber]')
        .forEach(setPhoneAttributes);

      wrapper.current.querySelectorAll('select').forEach(select => {
        toggleSelectPlaceholder(select);

        if (select.firstElementChild?.value === '') {
          select.firstElementChild.disabled = true;
        }
      });

      if (hasPrefillFields && wrapper.current) {
        hiddenFields.current = hiddenFieldKeys
          .map(key => {
            const el = wrapper.current.querySelector(`input[id*="${key}"]`);

            if (!el) {
              return null;
            }

            if (el.type !== 'hidden' && !config.enableDebugMode) {
              el.type = 'hidden';

              const section = el.closest('.ktc-default-section');

              if (section) {
                if (!section.classList.contains(styles.hiddenFields)) {
                  section.querySelectorAll('label').forEach(label => {
                    label.parentElement.removeChild(label);
                  });
                }

                section.classList.add(styles.hiddenFields);
                section.setAttribute('aria-hidden', 'true');
              }
            }

            return el ? { el, key } : null;
          })
          .filter(f => f);
      }
    });
  }, [
    hasPrefillFields,
    config.enableDebugMode,
    mutateDOM,
    toggleSelectPlaceholder
  ]);

  const prevProduct = useRef();
  const prevLoanDetails = useRef();
  const refreshTimer = useRef();

  const refreshForm = useCallback(
    (product, loanDetails, shouldInitElements) => {
      if (shouldInitElements) {
        initElements();
      }

      if (product && loanDetails) {
        if (
          !shouldInitElements &&
          product === prevProduct.current &&
          loanDetails === prevLoanDetails.current
        ) {
          return;
        }
        isMutatingDOM.current = true;

        clearTimeout(refreshTimer.current);
        const delay = config.enableDebugMode ? 100 : 650;

        refreshTimer.current = setTimeout(() => {
          mutateDOM(() => {
            const data = getHiddenFieldData(product, loanDetails);

            prevProduct.current = product;
            prevLoanDetails.current = loanDetails;

            hiddenFields.current.forEach(({ el, key }) => {
              if (data.hasOwnProperty(key)) {
                el.value = data[key];
              }
            });
          });
        }, delay);
      }
    },
    [initElements, mutateDOM, config]
  );

  useEffect(() => {
    let observer = null;

    if (hasAddedForm) {
      initElements();

      observer = new MutationObserver(e => {
        if (!isMutatingDOM.current) {
          refreshForm(prevProduct.current, prevLoanDetails.current, true);
        }
      });

      observer.observe(wrapper.current, {
        subtree: true,
        childList: true
      });
    }

    return () => observer?.disconnect();
  }, [hasAddedForm, initElements, refreshForm]);

  useEffect(() => {
    if ((product && loanDetails) || !hasPrefillFields) {
      refreshForm(product, loanDetails);
    } else {
      clearTimeout(refreshTimer.current);
    }
  }, [product, loanDetails, hasPrefillFields, refreshForm]);

  const resetPhoneAttributes = useCallback(e => {
    const el = e.target;
    const id = el.id?.toLowerCase();

    if (id?.includes('phonenumber')) {
      setPhoneAttributes(el);
    }
  }, []);

  const handleChange = useCallback(
    e => {
      if (e.target.tagName === 'SELECT') {
        toggleSelectPlaceholder(e.target);
      }
    },
    [toggleSelectPlaceholder]
  );

  return (
    <div
      ref={wrapper}
      className={className}
      onBlur={resetPhoneAttributes}
      onMouseOut={resetPhoneAttributes}
      onInput={handleChange}
    >
      {header && <h3>{header}</h3>}
      {form}
    </div>
  );
});

function setPhoneAttributes(el) {
  el.placeholder = 'Enter a phone number';

  if (el.type !== 'tel') {
    el.type = 'tel';
  }
}

function getHiddenFieldData(product, loanDetails) {
  if (!product || !loanDetails) {
    return {};
  }

  return {
    LoanPurpose: upperfirst(loanDetails.loanPurpose),
    PlanDescription: product.planDescription,
    PlanNumber: product.planNumber,
    QuotedRate: product.rate.toFixed(3),
    QuotedAPR: product.apr.toFixed(3),
    QuotedPayment: product.payment,
    LoanAmount: loanDetails.loanAmount,
    EstimatedValue: loanDetails.homeValue,
    ZipCode: loanDetails.zipCode,
    CreditScore: loanDetails.creditScore,
    PropertyType: loanDetails.propertyType,
    OccupancyType: loanDetails.occupancyType
  };
}

const hiddenFieldKeys = [
  'LoanPurpose',
  'PlanDescription',
  'PlanNumber',
  'QuotedRate',
  'QuotedAPR',
  'QuotedPayment',
  'LoanAmount',
  'EstimatedValue',
  'ZipCode',
  'CreditScore',
  'PropertyType',
  'OccupancyType'
];

const Form = React.memo(
  ({ setHasAddedForm, selector = '#mortgage-rate-calculator-form' }) => {
    const ref = useRef();
    const hasInitialized = useRef(false);

    useEffect(() => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;

        const initForm = () => {
          const formEl = document.querySelector(selector);

          if (formEl) {
            ref.current.appendChild(formEl);

            formEl.className = '';
            formEl.style.display = null;
          }

          setHasAddedForm(true);
        };

        if (document.readyState === 'interactive') {
          initForm();
        } else {
          document.addEventListener('DOMContentLoaded', initForm);
        }
      }
    }, [setHasAddedForm, selector]);

    return <div
      ref={ref}
      className={styles.form}
    />;
  }
);
